import { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const STAGE_LABELS = {
  LEAD: "LEAD",
  AGENT_ASSIGNED: "ASSIGNED",
  DOCUMENT_COLLECTION: "DOCS",
  INITIAL_FEE_PAID: "FEE PAID",
  SUBMITTED: "SUBMITTED",
  OFFER_RECEIVED: "OFFER",
  DEPOSIT_PAID: "DEPOSIT",
  OFFICIAL_ACCEPTANCE: "ACCEPTED",
  VISA_APPLICATION: "VISA APP",
  VISA_OUTCOME: "VISA OUTCOME",
  TRAVEL_PREP: "TRAVEL",
  ARRIVED: "ARRIVED",
  CLOSED: "CLOSED",
};

const ACTIVITY_ICONS = {
  application: {
    icon: "person_add",
    bg: "bg-emerald-50",
    color: "text-emerald-600",
  },
  stage_change: {
    icon: "verified",
    bg: "bg-[#00255a]/5",
    color: "text-[#00255a]",
  },
  opening: { icon: "school", bg: "bg-[#365da3]/10", color: "text-[#365da3]" },
  payment: { icon: "payments", bg: "bg-[#ff8483]/10", color: "text-[#862026]" },
};

// Builds an SVG path string from an array of { count } points, normalized to an 800x200 viewbox
const buildLinePath = (points) => {
  if (points.length === 0) return "";
  const max = Math.max(...points.map((p) => p.count), 1);
  const stepX = 800 / (points.length - 1 || 1);
  return points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${i * stepX},${180 - (p.count / max) * 160}`,
    )
    .join(" ");
};

export default function AdminOverviewPage() {
  const [overview, setOverview] = useState(null);
  const [byCountry, setByCountry] = useState([]);
  const [byStage, setByStage] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/admin/analytics/overview`, { headers }).then((r) =>
        r.json(),
      ),
      fetch(`${API_BASE}/admin/analytics/by-country`, { headers }).then((r) =>
        r.json(),
      ),
      fetch(`${API_BASE}/admin/analytics/by-stage`, { headers }).then((r) =>
        r.json(),
      ),
      fetch(`${API_BASE}/admin/analytics/monthly`, { headers }).then((r) =>
        r.json(),
      ),
      fetch(`${API_BASE}/admin/analytics/recent-activity`, { headers }).then(
        (r) => r.json(),
      ),
    ])
      .then(([ov, country, stage, month, act]) => {
        setOverview(ov.success ? ov.data : null);
        setByCountry(country.success ? country.data : []);
        setByStage(stage.success ? stage.data : []);
        setMonthly(month.success ? month.data : []);
        setActivity(act.success ? act.data : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleExportCsv = () => {
    const rows = [];

    rows.push(["IASC Consultancy — Dashboard Report"]);
    rows.push([`Generated: ${new Date().toLocaleString()}`]);
    rows.push([]);

    rows.push(["Overview"]);
    rows.push(["Total Applications", overview?.totalApplications ?? 0]);
    rows.push(["Pending Approvals", overview?.pendingApprovals ?? 0]);
    rows.push(["Active Openings", overview?.activeOpenings ?? 0]);
    rows.push(["Agents Active", overview?.activeAgents ?? 0]);
    rows.push([]);

    rows.push(["Top Countries", "Applications"]);
    byCountry.forEach((c) => rows.push([c.country, c.count]));
    rows.push([]);

    rows.push(["Pipeline Stage (Cumulative)", "Applications Reached"]);
    byStage.forEach((s) =>
      rows.push([STAGE_LABELS[s.stage] || s.stage, s.count]),
    );
    rows.push([]);

    rows.push(["Month", "New Applications"]);
    monthly.forEach((m) => rows.push([m.month, m.count]));
    rows.push([]);

    rows.push(["Recent Activity"]);
    activity.forEach((a) =>
      rows.push([new Date(a.createdAt).toLocaleString(), a.message]),
    );

    // Escape values containing commas/quotes per standard CSV rules
    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const str = String(cell ?? "");
            return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `iasc-dashboard-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  };

  const handleExportPdf = async () => {
    const token = localStorage.getItem("adminToken");
    const res = await fetch(`${API_BASE}/admin/analytics/export-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `iasc-dashboard-report-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  };

  if (loading)
    return (
      <p className="text-center py-40 text-[#64748b]">Loading dashboard...</p>
    );

  const maxCountry = Math.max(...byCountry.map((c) => c.count), 1);
  const maxStage = Math.max(...byStage.map((s) => s.count), 1);
  const linePath = buildLinePath(monthly);
  const conversionRate =
    byStage.length > 0
      ? (
          (byStage[byStage.length - 1]?.count / byStage[0]?.count) * 100 || 0
        ).toFixed(1)
      : "0.0";

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-0 md:ml-64 min-h-screen p-8">
        <div className="p-8">
          <div className="mb-8 flex justify-between items-end flex-wrap gap-4">
            <div>
              <h2 className="text-3xl font-bold text-[#00255a] mb-1">
                Dashboard Overview
              </h2>
              <p className="text-[#64748b]">
                Welcome back, Admin. Here's what's happening today.
              </p>
            </div>
            <div className="flex gap-3">
              <button className="px-4 py-2 bg-white border border-[#e2e8f0] rounded-lg text-sm font-semibold text-[#00255a] flex items-center gap-2 hover:bg-[#f2f4f6] transition-colors">
                <span className="material-symbols-outlined text-lg">
                  calendar_today
                </span>
                Last 30 Days
              </button>
              <div className="relative">
                <button
                  onClick={() => setExportMenuOpen(!exportMenuOpen)}
                  className="px-4 py-2 bg-[#00255a] text-white rounded-lg text-sm font-semibold flex items-center gap-2 shadow-md hover:opacity-90 transition-opacity"
                >
                  <span className="material-symbols-outlined text-lg">
                    download
                  </span>
                  Export Report
                  <span className="material-symbols-outlined text-lg">
                    expand_more
                  </span>
                </button>
                {exportMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setExportMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-[#e2e8f0] z-20 py-2">
                      <button
                        onClick={handleExportCsv}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#f2f4f6] flex items-center gap-2 text-[#44474f] text-sm"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          table_view
                        </span>
                        Export as CSV
                      </button>
                      <button
                        onClick={handleExportPdf}
                        className="w-full text-left px-4 py-2.5 hover:bg-[#f2f4f6] flex items-center gap-2 text-[#44474f] text-sm"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          picture_as_pdf
                        </span>
                        Export as PDF
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon="description"
              iconBg="bg-[#00255a]/5"
              iconColor="text-[#00255a]"
              label="Total Applications"
              value={overview?.totalApplications ?? 0}
            />
            <StatCard
              icon="pending_actions"
              iconBg="bg-[#79161e]/10"
              iconColor="text-[#862026]"
              label="Pending Approvals"
              value={overview?.pendingApprovals ?? 0}
            />
            <StatCard
              icon="work"
              iconBg="bg-[#365da3]/5"
              iconColor="text-[#365da3]"
              label="Active Openings"
              value={overview?.activeOpenings ?? 0}
            />
            <StatCard
              icon="support_agent"
              iconBg="bg-[#ff8483]/10"
              iconColor="text-[#862026]"
              label="Agents Active"
              value={overview?.activeAgents ?? 0}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Charts Column */}
            <div className="lg:col-span-8 space-y-8">
              {/* Monthly Trends Line Chart */}
              <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)]">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-lg font-bold text-[#00255a]">
                    Monthly Applications
                  </h4>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[#00255a]" />
                    <span className="text-sm text-[#64748b]">Applications</span>
                  </div>
                </div>
                {monthly.length === 0 ? (
                  <p className="text-center text-[#64748b] py-16">
                    No data yet.
                  </p>
                ) : (
                  <div className="h-64 relative border-b border-[#e2e8f0] pb-8">
                    <svg
                      className="absolute inset-0 w-full h-full"
                      preserveAspectRatio="none"
                      viewBox="0 0 800 200"
                    >
                      <path
                        d={linePath}
                        fill="none"
                        stroke="#00255a"
                        strokeLinecap="round"
                        strokeWidth="4"
                        vectorEffect="non-scaling-stroke"
                      />
                      <path
                        d={`${linePath} V200 H0 Z`}
                        fill="url(#lineGradient)"
                        opacity="0.1"
                        vectorEffect="non-scaling-stroke"
                      />
                      <defs>
                        <linearGradient
                          id="lineGradient"
                          x1="0"
                          x2="0"
                          y1="0"
                          y2="1"
                        >
                          <stop offset="0%" stopColor="#00255a" />
                          <stop offset="100%" stopColor="#fff" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute bottom-0 w-full flex justify-between text-[10px] text-[#64748b] font-bold px-2">
                      {monthly.map((m) => (
                        <span key={m.month}>{m.month}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Top Countries */}
                <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)]">
                  <h4 className="text-lg font-bold text-[#00255a] mb-6">
                    Top Countries
                  </h4>
                  {byCountry.length === 0 ? (
                    <p className="text-[#64748b] text-sm">No data yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {byCountry.map((c) => (
                        <div key={c.country} className="space-y-2">
                          <div className="flex justify-between text-sm font-semibold">
                            <span>{c.country}</span>
                            <span>{c.count}</span>
                          </div>
                          <div className="w-full bg-[#eceef0] rounded-full h-2">
                            <div
                              className="bg-[#00255a] h-full rounded-full"
                              style={{
                                width: `${(c.count / maxCountry) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pipeline Funnel */}
                <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)]">
                  <h4 className="text-lg font-bold text-[#00255a] mb-6">
                    Pipeline Funnel
                  </h4>
                  {byStage.length === 0 ? (
                    <p className="text-[#64748b] text-sm">No data yet.</p>
                  ) : (
                    <div className="flex flex-col items-center space-y-1">
                      {byStage.map((s, i) => {
                        const widthPct = 100 - i * (40 / byStage.length);
                        const opacity = 1 - i * (0.4 / byStage.length);
                        return (
                          <div
                            key={s.stage}
                            className="py-2 text-center text-white font-bold text-[10px]"
                            style={{
                              width: `${widthPct}%`,
                              background: "#00255a",
                              opacity,
                              borderRadius:
                                i === 0
                                  ? "8px 8px 0 0"
                                  : i === byStage.length - 1
                                    ? "0 0 8px 8px"
                                    : 0,
                            }}
                          >
                            {STAGE_LABELS[s.stage] || s.stage} ({s.count})
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-6 flex justify-between px-2 text-[10px] font-bold text-[#64748b]">
                    <span>Conversion: {conversionRate}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar: Activity Feed */}
            <div className="lg:col-span-4">
              <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)] h-full flex flex-col">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="text-lg font-bold text-[#00255a]">
                    Recent Activity
                  </h4>
                </div>
                <div className="flex-1 space-y-6 overflow-y-auto pr-2">
                  {activity.length === 0 ? (
                    <p className="text-[#64748b] text-sm">
                      No recent activity.
                    </p>
                  ) : (
                    activity.map((item, i) => {
                      const style =
                        ACTIVITY_ICONS[item.type] ||
                        ACTIVITY_ICONS.stage_change;
                      return (
                        <div key={i} className="flex gap-4">
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full ${style.bg} ${style.color} flex items-center justify-center`}
                            >
                              <span className="material-symbols-outlined text-sm">
                                {style.icon}
                              </span>
                            </div>
                            {i < activity.length - 1 && (
                              <div className="w-px h-full bg-[#e2e8f0] mt-2" />
                            )}
                          </div>
                          <div className="pb-6 border-b border-[#e2e8f0]/50 w-full">
                            <p className="text-sm text-[#0f172a]">
                              {item.message}
                            </p>
                            <p className="text-[10px] text-[#64748b] mt-2 font-bold uppercase tracking-widest">
                              {timeAgo(item.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, iconBg, iconColor, label, value }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-[#e2e8f0] shadow-[0px_10px_30px_rgba(30,60,114,0.05)] hover:shadow-[0px_20px_40px_rgba(30,60,114,0.12)] hover:-translate-y-0.5 transition-all">
      <div className={`inline-flex p-3 ${iconBg} ${iconColor} rounded-xl mb-4`}>
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <p className="text-[#64748b] text-sm mb-1">{label}</p>
      <h3 className="text-2xl font-bold text-[#00255a]">
        {value.toLocaleString()}
      </h3>
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} Minutes Ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} Hours Ago`;
  return new Date(dateStr).toLocaleDateString();
}
